import React, { useState } from "react";
import { RefreshCw, Truck, CheckCircle2, PackageSearch, ShoppingCart } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, fmtIDR, wibDateISO } from "../../lib/api";
import { Panel } from "../../lib/ui";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { DataTable, StatCard, TabBar, TableSkeleton, platformBadge, timeStr, num, PLATFORM_FILTER } from "./shared";

export default function ShippedView({ write }) {
  const [date, setDate] = useState(wibDateISO());
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("matched");
  const [confirm, setConfirm] = useState(null);
  const [checkingOut, setCheckingOut] = useState(false);

  const sync = async () => {
    setLoading(true);
    try {
      const r = await api.post("/desty/sync-shipped", { date });
      setData(r.data);
      const s = r.data.summary || {};
      toast.success(`Sync selesai: ${s.total_shipped || 0} terkirim, ${s.matched || 0} cocok Keep`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const doCheckout = async () => {
    if (!confirm) return;
    setCheckingOut(true);
    try {
      const r = await api.post("/desty/checkout", { keep_id: confirm.keep.id, order_sn: confirm.order_sn, platform: confirm.platform });
      toast.success(`Keep ${r.data.keep_number} → Sales ${r.data.sale_number} berhasil`);
      setConfirm(null);
      sync();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setCheckingOut(false); }
  };

  const s = data?.summary || {};
  const rows = data ? (data[tab] || []) : [];
  const TABS = [
    { key: "matched", label: "Cocok di Keep ", icon: CheckCircle2, count: data?.matched?.length || 0 },
    { key: "unmatched", label: "Belum di Keep ", icon: PackageSearch, count: data?.unmatched?.length || 0 },
    { key: "already_sale", label: "Sudah jadi Sales ", icon: ShoppingCart, count: data?.already_sale?.length || 0 },
  ];

  const base = [
    { key: "order_sn", header: "Order ID (Desty)", className: "font-mono text-xs", sortAccessor: (o) => o.order_sn },
    { key: "platform", header: "Platform", render: (o) => platformBadge(o.platform) },
    { key: "store_name", header: "Toko", className: "text-sm", render: (o) => o.store_name || "-" },
    { key: "update_time", header: "Jam Kirim", className: "text-sm", sortAccessor: (o) => o.update_time || 0, render: (o) => timeStr(o.update_time) },
  ];
  const matchedCols = [
    { key: "keep_number", header: "No Keep", className: "font-medium", render: (o) => o.keep?.number },
    { key: "customer", header: "Customer", className: "text-sm", render: (o) => o.keep?.customer_name },
    { key: "qty", header: "Qty", align: "right", sortAccessor: (o) => o.keep?.remaining_qty || 0, render: (o) => num(o.keep?.remaining_qty) },
    { key: "value", header: "Nilai", align: "right", sortAccessor: (o) => o.keep?.remaining_value || 0, render: (o) => fmtIDR(o.keep?.remaining_value) },
    { key: "aksi", header: "Aksi", align: "right", render: (o) => (
      write ? <Button size="sm" onClick={() => setConfirm(o)} className="bg-cocoa hover:bg-cocoa-dark text-ivory h-8" data-testid={`desty-checkout-${o.order_sn}`}>
        <ShoppingCart className="w-3.5 h-3.5 mr-1" /> Konversi</Button> : null
    ) },
  ];
  const saleCols = [{ key: "sale_number", header: "No Sales", className: "font-medium", render: (o) => o.sale_number }];
  const columns = tab === "matched" ? [...base, ...matchedCols] : tab === "already_sale" ? [...base, ...saleCols] : base;

  return (
    <>
      <div className="flex justify-end items-center gap-2">
        <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-40 border-cocoa/30" data-testid="desty-date-input" />
        <Button onClick={sync} disabled={loading} className="bg-cocoa hover:bg-cocoa-dark text-ivory" data-testid="desty-sync-btn">
          <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? "animate-spin" : ""}`} />
          {loading ? "Menarik data..." : "Sync Terkirim"}
        </Button>
      </div>

      {loading && <TableSkeleton testid="desty-shipped-skeleton" />}

      {!loading && !data && (
        <Panel className="p-10 text-center text-cocoa/60" data-testid="desty-empty">
          <Truck className="w-10 h-10 mx-auto mb-3 opacity-40" />
          Pilih tanggal lalu klik <b>Sync Terkirim</b> untuk menarik order dari Desty.
        </Panel>
      )}

      {!loading && data && (
        <div className="space-y-5">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <StatCard label="Total Terkirim" value={s.total_shipped} tone="cocoa" />
            <StatCard label="Cocok di Keep" value={s.matched} tone="green" />
            <StatCard label="Belum di Keep" value={s.unmatched} tone="amber" />
            <StatCard label="Sudah jadi Sales" value={s.already_sale} tone="slate" />
          </div>
          <TabBar tabs={TABS} value={tab} onChange={setTab} prefix="desty-tab" />
          <DataTable testid="desty-table" columns={columns} rows={rows}
            searchAccessors={[(o) => o.order_sn, (o) => o.store_name, (o) => o.keep?.number, (o) => o.keep?.customer_name, (o) => o.sale_number]}
            searchPlaceholder="Cari Order ID / toko / no keep / customer…" filters={[PLATFORM_FILTER]}
            emptyText="Tidak ada data pada kategori ini." />
        </div>
      )}

      <Dialog open={!!confirm} onOpenChange={(v) => !v && setConfirm(null)}>
        <DialogContent data-testid="desty-confirm-dialog">
          <DialogHeader><DialogTitle>Konversi Keep ke Sales</DialogTitle></DialogHeader>
          {confirm && (
            <div className="space-y-2 text-sm">
              <p>Order Desty <b className="font-mono">{confirm.order_sn}</b> ({confirm.platform}) akan dikonversi dari Keep menjadi transaksi kasir.</p>
              <div className="rounded-lg bg-cocoa/5 p-3 space-y-1">
                <div className="flex justify-between"><span className="text-cocoa/60">No Keep</span><b>{confirm.keep?.number}</b></div>
                <div className="flex justify-between"><span className="text-cocoa/60">Customer</span><span>{confirm.keep?.customer_name}</span></div>
                <div className="flex justify-between"><span className="text-cocoa/60">Total Qty</span><span>{num(confirm.keep?.remaining_qty)} pcs</span></div>
                <div className="flex justify-between"><span className="text-cocoa/60">Nilai</span><b>{fmtIDR(confirm.keep?.remaining_value)}</b></div>
                <div className="flex justify-between"><span className="text-cocoa/60">Pembayaran</span><span className="capitalize">{confirm.platform}</span></div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirm(null)} data-testid="desty-confirm-cancel">Batal</Button>
            <Button onClick={doCheckout} disabled={checkingOut} className="bg-cocoa hover:bg-cocoa-dark text-ivory" data-testid="desty-confirm-ok">
              {checkingOut ? "Memproses..." : "Konversi ke Sales"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
